import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ChevronDown, List } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TOCHeading } from '@/lib/tableOfContents';
import { scrollToElementWithOffset } from '@/lib/scrollUtils';

interface MobileTOCProps {
  headings: TOCHeading[];
  activeHeading: string;
  className?: string;
}

export function MobileTOC({ headings, activeHeading, className }: MobileTOCProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleHeadingClick = (id: string) => {
    scrollToElementWithOffset(id, 120);
    setIsOpen(false); // Close the sheet after navigation
  };

  const renderHeading = (heading: TOCHeading) => {
    const isActive = activeHeading === heading.id;
    const indentLevel = Math.min(heading.level - 1, 3); // Max 4 levels of indentation

    return (
      <li key={heading.id}>
        <button
          onClick={() => handleHeadingClick(heading.id)}
          className={cn(
            'text-left w-full px-4 py-3 text-sm transition-colors hover:bg-muted',
            'border-l-2 border-transparent',
            isActive && 'bg-primary/10 border-primary text-primary font-medium',
            !isActive && 'text-muted-foreground hover:text-foreground',
          )}
          style={{
            paddingLeft: `${1 + (indentLevel * 0.75)}rem`,
          }}
        >
          <span className="block">
            {heading.text}
          </span>
        </button>
      </li>
    );
  };

  if (headings.length === 0) {
    return null;
  }

  return (
    <div className={cn('px-4 pb-3', className)}>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" size="sm" className="w-full justify-between max-w-md mx-auto">
            <div className="flex items-center gap-2">
              <List className="h-4 w-4" />
              <span>On this page</span>
            </div>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="max-h-[70vh] overflow-hidden">
          <SheetHeader className="pb-4">
            <SheetTitle className="flex items-center gap-2">
              <List className="h-4 w-4" />
              Table of Contents
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-auto">
            <nav>
              <ul className="space-y-0">
                {headings.map(renderHeading)}
              </ul>
            </nav>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}